// src/admin/AdminAds.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { 
  FiUpload, FiLink, FiCheckCircle, FiAlertCircle, FiX, FiImage, 
  FiPlus, FiEdit2, FiTrash2, FiRefreshCw 
} from 'react-icons/fi';
import styles from './AdminAds.module.css';

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ images: [], url: '', is_active: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  // Load all ads
  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ads')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) {
      setError('Failed to load ads: ' + error.message);
    } else {
      setAds(data || []);
    }
    setLoading(false);
  };

  const validateAspectRatio = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (Math.abs(ratio - 4/5) < 0.05) resolve();
        else reject(`Image must be 4:5 ratio (current: ${img.width}:${img.height})`);
      };
      img.onerror = () => reject('Failed to load image');
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImageToStorage = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `ad_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from('ads')
      .upload(filePath, file);
    if (uploadError) throw uploadError;
    const { data: { publicUrl } } = supabase.storage.from('ads').getPublicUrl(filePath);
    return publicUrl;
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (files.length > 2) {
      setError('Maximum 2 images per ad allowed.');
      return;
    }
    setError('');
    setUploading(true);
    const uploadedUrls = [];
    for (const file of files) {
      try {
        await validateAspectRatio(file);
        const url = await uploadImageToStorage(file);
        uploadedUrls.push(url);
      } catch (err) {
        setError(err);
        setUploading(false);
        return;
      }
    }
    setFormData(prev => ({ ...prev, images: uploadedUrls }));
    setUploading(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0) {
      setError('Please upload at least one image.');
      return;
    }
    if (!formData.url.trim()) {
      setError('Please enter a destination URL.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');

    let result;
    if (editingId) {
      // Update
      result = await supabase
        .from('ads')
        .update({
          images: formData.images,
          url: formData.url.trim(),
          is_active: formData.is_active,
          updated_at: new Date()
        })
        .eq('id', editingId);
    } else {
      // Insert
      result = await supabase
        .from('ads')
        .insert({
          images: formData.images,
          url: formData.url.trim(),
          is_active: formData.is_active,
          created_at: new Date(),
          updated_at: new Date()
        });
    }

    if (result.error) {
      setError('Failed to save ad: ' + result.error.message);
    } else {
      setSuccess(editingId ? 'Ad updated successfully!' : 'Ad added successfully!');
      setFormData({ images: [], url: '', is_active: true });
      setEditingId(null);
      fetchAds();
      setTimeout(() => setSuccess(''), 3000);
    }
    setSubmitting(false);
  };

  const handleEdit = (ad) => {
    setEditingId(ad.id);
    setFormData({
      images: ad.images || [],
      url: ad.url || '',
      is_active: ad.is_active !== undefined ? ad.is_active : true
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    const { error } = await supabase.from('ads').delete().eq('id', id);
    if (error) {
      setError('Failed to delete ad: ' + error.message);
    } else {
      setSuccess('Ad deleted successfully.');
      fetchAds();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const toggleActive = async (id, current) => {
    const { error } = await supabase
      .from('ads')
      .update({ is_active: !current, updated_at: new Date() })
      .eq('id', id);
    if (!error) fetchAds();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ images: [], url: '', is_active: true });
    setError('');
  };

  return (
    <div className={styles.adminAds}>
      <div className={styles.header}>
        <h2>Ad Manager</h2>
        <p className={styles.headerSubtitle}>Create, edit, and manage popup ads</p>
      </div>

      {/* Form */}
      <div className={styles.formCard}>
        <h3>{editingId ? 'Edit Ad' : 'Add New Ad'}</h3>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label}><FiImage /> Ad Images (1-2, 4:5 ratio)</label>
            <div className={styles.uploadArea}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                id="adImageUpload"
                style={{ display: 'none' }}
              />
              <label htmlFor="adImageUpload" className={styles.uploadButton}>
                <FiUpload /> {uploading ? 'Uploading...' : 'Upload Images'}
              </label>
            </div>
            {formData.images.length > 0 && (
              <div className={styles.imagePreviewList}>
                {formData.images.map((img, idx) => (
                  <div key={idx} className={styles.imagePreviewItem}>
                    <img src={img} alt={`preview ${idx}`} />
                    <button onClick={() => removeImage(idx)} className={styles.removeImageBtn}><FiX /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}><FiLink /> Destination URL</label>
            <input
              type="url"
              className={styles.urlInput}
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://example.com"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Active (visible to customers)
            </label>
          </div>

          {error && <div className={styles.errorMessage}><FiAlertCircle /> {error}</div>}
          {success && <div className={styles.successMessage}><FiCheckCircle /> {success}</div>}

          <div className={styles.formActions}>
            {editingId && (
              <button type="button" onClick={cancelEdit} className={styles.cancelBtn}>Cancel</button>
            )}
            <button type="submit" className={styles.submitBtn} disabled={submitting || uploading}>
              {submitting ? 'Saving...' : editingId ? 'Update Ad' : 'Add Ad'}
            </button>
          </div>
        </form>
      </div>

      {/* List of Ads */}
      <div className={styles.listContainer}>
        <h3>All Ads</h3>
        {loading ? (
          <div className={styles.loadingSpinner}></div>
        ) : ads.length === 0 ? (
          <div className={styles.emptyState}>No ads created yet.</div>
        ) : (
          <div className={styles.adList}>
            {ads.map((ad) => (
              <div key={ad.id} className={styles.adCard}>
                <div className={styles.adPreview}>
                  {ad.images && ad.images.length > 0 ? (
                    <img src={ad.images[0]} alt="ad preview" className={styles.adThumbnail} />
                  ) : (
                    <div className={styles.noImage}>No image</div>
                  )}
                </div>
                <div className={styles.adDetails}>
                  <div className={styles.adInfo}>
                    <div className={styles.adUrl}><FiLink /> {ad.url}</div>
                    <div className={styles.adImagesCount}>{ad.images?.length || 0} image(s)</div>
                  </div>
                  <div className={styles.adActions}>
                    <span className={ad.is_active ? styles.activeBadge : styles.inactiveBadge}>
                      {ad.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button onClick={() => toggleActive(ad.id, ad.is_active)} className={styles.toggleBtn}>
                      <FiRefreshCw />
                    </button>
                    <button onClick={() => handleEdit(ad)} className={styles.editBtn}>
                      <FiEdit2 />
                    </button>
                    <button onClick={() => handleDelete(ad.id)} className={styles.deleteBtn}>
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAds;